import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Avatar from '../../../component/avatar/Avatar';
import {numberCompactFormat} from '../../../util/locale';

import './Profile.css';
import ProfileButtons from "./ProfileButtons";
import useMediaQuery from "../../../hook/useMediaQuery";
import CertifiedSellerBadge from '../../../component/icons/CertifiedSellerBadge';

/**
 * Seller profile component.
 */
export default function Profile({ hostname, seller, userAgent, isMe = false, sellerTab }) {
    const isMobileSize =useMediaQuery("(max-width: 767px)");
    // Bio read-more state
    const [isBioExpanded, setIsBioExpanded] = useState(false);
    const [isBioClamped, setIsBioClamped] = useState(false);
    const bioRef = useRef(null);

    useEffect(() => {
        const el = bioRef.current;
        if (!el) return;
        if (!isBioExpanded) {
            const id = requestAnimationFrame(() => {
                // if content is taller than the clamped box, show the toggle
                setIsBioClamped(el.scrollHeight > el.clientHeight + 1);
            });
            return () => cancelAnimationFrame(id);
        } else {
            // When expanded, keep the toggle visible to allow collapse
            setIsBioClamped(true);
        }
    }, [seller.user.bio, isBioExpanded]);
    // build the UI elements
    return (
        <div className="Profile">
            {/* hidden h1 */}
            <h1 className="sr-only">{`${seller.user.nickname} | 빈티지 세컨핸드 패션 플랫폼`}</h1>

            {seller.user.resizedBigCoverImage && <div className="Profile-cover">
                <img className="Profile-cover-image"
                     loading="eager"
                     fetchPriority="high"
                     width="1200"
                     height="400"
                     alt={`${seller.user.nickname} 커버 이미지`}
                     src={seller.user.resizedBigCoverImage} />
            </div>}

            <div className="Profile-header">
                <div className="Profile-header-info">
                    <div className="Profile-info">
                        <div className="Profile-image">
                            <Avatar
                                user={{...seller.user, resizedSmallImage: seller.user.resizedBigImage}}
                                loading="eager"
                            />
                        </div>
                        <div className="Profile-header-right">
                            <div className="Profile-nickname font-nhaasgrotesk">
                                {seller.user.nickname}
                                {seller.is_certified && (
                                    <CertifiedSellerBadge className="Profile-seller-badge" />
                                )}
                            </div>
                            <h2 className="Profile-username">
                                {`@${seller.user.username}`}
                            </h2>
                            <div className="Profile-stats">
                                <div className="stat">
                                    <span className="name">팔로워</span>
                                    <span className="number">{numberCompactFormat(seller.user.follower_count)}</span>
                                </div>
                                {seller.productCount != null && (
                                    <div className="stat">
                                        <span className="name">판매수</span>
                                        <span className="number">{numberCompactFormat(seller.productCount)}</span>
                                    </div>
                                )}
                                {seller.rating &&
                                <div className="stat">
                                    <span className="name">평점</span>
                                    <div className="rating">
                                        <svg
                                            className="rating-star"
                                            viewBox="0 0 16 16"
                                            width="16"
                                            height="16"
                                            aria-hidden="true"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8.63057 0.783208C8.41245 0.199892 7.58733 0.199844 7.3692 0.783208L5.7196 5.19479L1.01418 5.4004C0.392011 5.42759 0.136989 6.21231 0.624394 6.60003L4.31031 9.53215L3.0518 14.0708C2.88539 14.6709 3.5529 15.156 4.07227 14.8122L7.99988 12.2128L11.9275 14.8122C12.4468 15.1559 13.1144 14.671 12.948 14.0708L11.6895 9.53215L15.3754 6.60003C15.8627 6.21234 15.6078 5.42759 14.9856 5.4004L10.2802 5.19479L8.63057 0.783208Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        <span className="number">{seller.rating ? seller.rating.toFixed(1) : '-'}</span>
                                    </div>
                                </div>}
                            </div>
                        </div>
                    </div>
                    {!isMobileSize && <ProfileButtons isMe={isMe} userAgent={userAgent} hostname={hostname}
                                                      seller={seller} sellerTab={sellerTab}
                                                      sellertab={sellerTab}
                                                      text={`${seller.user.nickname} (@${seller.user.username}) on FruitsFamily`} />}
                </div>
                <div className="Profile-bio">
                    {seller.user.bio && (
                        <>
                            <p
                                ref={bioRef}
                                className={`Profile-bio ${!isBioExpanded ? 'Profile-bio--clamp' : ''}`}
                            >
                                {seller.user.bio}
                            </p>
                            {isBioClamped && (
                                <button
                                    type="button"
                                    className="Profile-bio-toggle"
                                    onClick={() => setIsBioExpanded(v => !v)}
                                >
                                    {isBioExpanded ? '간략히 보기' : '더보기'}
                                </button>
                            )}
                        </>
                    )}
                </div>
                {isMobileSize && <ProfileButtons isMe={isMe} userAgent={userAgent} hostname={hostname}
                                                 seller={seller} sellerTab={sellerTab}
                                                 sellertab={sellerTab}
                                                 text={`${seller.user.nickname} (@${seller.user.username}) on FruitsFamily`} />}
            </div>
        </div>
    );
}
Profile.propTypes = {
    hostname: PropTypes.string.isRequired,
    seller: PropTypes.shape({
        id:  PropTypes.string.isRequired,
        rating: PropTypes.number,
        productCount: PropTypes.number,
        is_certified: PropTypes.bool,
        user: PropTypes.shape({
            username: PropTypes.string.isRequired,
            nickname: PropTypes.string.isRequired,
            bio: PropTypes.string,
            resizedBigImage: PropTypes.string,
            resizedBigCoverImage: PropTypes.string,
            follower_count: PropTypes.number.isRequired,
            following_count: PropTypes.number.isRequired,
            isBusiness: PropTypes.bool,
        }),
        seller: PropTypes.shape({
            is_tester: PropTypes.bool,
            is_certified: PropTypes.bool,
            product_count: PropTypes.number,
        }),
    }).isRequired,
    userAgent: PropTypes.string.isRequired,
    isMe: PropTypes.bool,
    sellerTab: PropTypes.symbol
};
